import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommentList from "./CommentList";
import Attachments from "./Attachments";
import { Box, Grid } from "@mui/system";
import { useComments } from "./discussionHooks/useComments";
import { useEffect } from "react";
import {
  resetOfCommentsAndFiles,
  setDownloadFileTrackID,
  setModuleType,
  setObjectSharedUid,
  setObjectType,
  setPostCommentTrackID,
  setPreviewFileTrackID,
  setReactionTrackID,
  setReferenceModule,
  setUploadFileTrackID,
} from "../../features/genericCommentsAndFilesSlice";
import { useDispatch } from "react-redux";
import { useFileUploads } from "./discussionHooks/useFileUploads";
import ExpandableCommentBox from "./ExpandableCommentBox";
import { selectUserToken } from "../../features/userSlice";
import { useSelector } from "react-redux";
import { DiscussionCommentsAndFilesEndPointLogic } from "../../logic/GeneralDiscussion/DiscussionCommentsAndFilesEndPointLogic";
type Props = {
  objectType: string;
  referenceModule: string;
  moduleType: any;
  objectSharedUid: any; // as it can be multiple shared uids for multi meta (current requirement)
  postCommentTrackID: any; // Track ID for posting comments
  reactionTrackID: any; // Track ID for reactions
  uploadFileTrackID: any; // Track ID for uploading files
  downloadFileTrackID: any; // Track ID for downloading files
  previewFileTrackID: any; // Track ID for previewing files
};
const CommentsAndFiles = ({
  objectType,
  referenceModule,
  moduleType,
  objectSharedUid,
  postCommentTrackID,
  reactionTrackID,
  uploadFileTrackID,
  previewFileTrackID,
  downloadFileTrackID,
}: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as string;
  const { commentList, addComment, handleUpdateComment, handleDeleteComment } =
    useComments(); // custom hook for comments
  const {
    handleFileUpload,
    handleRemoveFile,
    handleFileDrop,
    isDragging,
    setIsDragging,
    files,
    fileUploadLoading,
    setFileUploadLoading,
  } = useFileUploads(); // custom hook for files

  // Set the object type, reference module and module type, tracking id in the redux store(Avoid prop drilling)
  useEffect(() => {
    dispatch(setObjectType(objectType)); // General Discussion object type
    dispatch(setReferenceModule(referenceModule)); // General Discussion reference module
    dispatch(setModuleType(moduleType)); // General Discussion module type
    dispatch(setObjectSharedUid(objectSharedUid)); //shared uid specific to the object
    dispatch(setPostCommentTrackID(postCommentTrackID)); // Track ID for posting comments
    dispatch(setReactionTrackID(reactionTrackID)); // Track ID for reactions
    dispatch(setUploadFileTrackID(uploadFileTrackID)); // Track ID for uploading files
    dispatch(setPreviewFileTrackID(previewFileTrackID)); // Track ID for previewing files
    dispatch(setDownloadFileTrackID(downloadFileTrackID)); // Track ID for downloading files

    return () => {
      dispatch(resetOfCommentsAndFiles());
    };

    // eslint-disable-next-line
  }, [
    objectType,
    referenceModule,
    moduleType,
    objectSharedUid,
    postCommentTrackID,
    reactionTrackID,
    uploadFileTrackID,
    previewFileTrackID,
    downloadFileTrackID,
  ]);

  const getUserList = async (oauthTokenHeader: string) => {
    await DiscussionCommentsAndFilesEndPointLogic.getAllUserList(
      oauthTokenHeader
    );
  };

  //-- Get All Users
  useEffect(() => {
    if (!oauthTokenHeader) return;
    getUserList(oauthTokenHeader);
    // eslint-disable-next-line
  }, []);

  return (
    <Box mt={2} mb={2}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{ minHeight: "48px !important" }}
        >
          <Typography component="span" sx={{ fontSize: "18px" }}>
            Discussion
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={4}>
            <Grid size={9}>
              {/* expandable comment box */}
              <ExpandableCommentBox addComment={addComment} />
              {/* Comments */}
              <CommentList
                commentList={commentList}
                handleUpdateComment={handleUpdateComment}
                handleDeleteComment={handleDeleteComment}
              />
            </Grid>
            <Grid size={3}>
              <Attachments
                isDragging={isDragging}
                setIsDragging={setIsDragging}
                handleFileDrop={handleFileDrop}
                handleFileUpload={handleFileUpload}
                fetchedFiles={files.length > 0 ? files : []}
                handleRemoveFile={handleRemoveFile}
                fileUploadLoading={fileUploadLoading}
                setFileUploadLoading={setFileUploadLoading}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CommentsAndFiles;
